import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Clock,
  ShieldCheck,
  Building2,
  MessageCircle,
  Compass,
  Activity,
  Flame,
  Users,
  HeartHandshake,
  ShieldAlert,
  LifeBuoy,
} from "lucide-react";
import usePageMeta from "../../lib/usePageMeta";
import BreadcrumbSchema from "../../components/public/BreadcrumbSchema";
import AccessModelCallout from "../../components/public/AccessModelCallout";

const WORKPLACE_CHALLENGE_CARDS = [
  {
    icon: Activity,
    label: "Employee Disengagement",
    body: "Only 31% of U.S. employees were actively engaged at work in 2025, according to Gallup. Personal stress is not the only cause of disengagement, but employees do not leave the rest of life at the door when they come to work.",
    source: {
      label: "Gallup — U.S. Employee Engagement (2025)",
      href: "https://www.gallup.com/workplace/643286/employee-engagement-sinks-year-low.aspx",
    },
  },
  {
    icon: Flame,
    label: "Burnout & Overwhelm",
    body: "NAMI's 2026 workplace survey found that 53% of employees reported feeling burned out because of their job, while 39% said they had felt so overwhelmed that it was difficult to do their work.",
    source: {
      label: "NAMI — 2026 Workplace Mental Health Poll",
      href: "https://www.nami.org/support-education/publications-reports/survey-reports/nami-workplace-mental-health-poll-2024/",
    },
  },
  {
    icon: Users,
    label: "Personal & Family Pressures",
    body: "Grief, serious illness, divorce or separation, financial strain, relationship difficulties, and major life changes can follow employees into the workplace.",
  },
  {
    icon: HeartHandshake,
    label: "Caregiving Stress",
    body: "Employees caring for children, aging parents, spouses, or other loved ones may carry significant emotional and practical pressure alongside their work responsibilities. Current workplace data continues to show that employees with caregiving responsibilities report elevated levels of burnout and overwhelm.",
    source: {
      label: "NAMI Workplace Mental Health data — caregivers",
      href: "https://www.nami.org/support-education/publications-reports/survey-reports/nami-workplace-mental-health-poll-2024/",
    },
  },
  {
    icon: ShieldAlert,
    label: "Domestic Violence & Safety Concerns",
    body: "Domestic and intimate-partner violence can affect attendance, concentration, productivity, safety, and overall workplace well-being.",
    source: {
      label: "CDC — Intimate Partner Violence Prevention",
      href: "https://www.cdc.gov/intimate-partner-violence/about/index.html",
    },
  },
  {
    icon: LifeBuoy,
    label: "Substance Use & Family Concerns",
    body: "Substance-use concerns may affect employees directly or through someone they love. Current SAMHSA data continues to show substance-use disorders affect a substantial share of U.S. adults each year. CCCHAPS provides non-clinical support and can encourage appropriate professional resources when needs extend beyond chaplaincy scope.",
    source: {
      label: "SAMHSA — National Survey on Drug Use and Health",
      href: "https://www.samhsa.gov/data/release/2023-national-survey-drug-use-and-health-nsduh-releases",
    },
  },
];

/**
 * Public — Workplace Chaplaincy service page.
 * Route: /services/workplace-chaplaincy
 *
 * IMAGE ASSETS
 * All four images are served from /public/images/workplace-chaplaincy/.
 * Each was sourced from Unsplash under the standard Unsplash License
 * (free for commercial and non-commercial use, no attribution required).
 * See report for photo IDs and licensing basis. No external hotlinking.
 */
const IMAGES = {
  hero: {
    src: "/images/workplace-chaplaincy/hero.jpg",
    alt: "Two colleagues standing at a workstation in a modern office having a natural, engaged conversation.",
  },
  trustPresence: {
    src: "/images/workplace-chaplaincy/trust-presence.jpg",
    alt: "Two coworkers sitting at a small office table in a quiet, supportive one-on-one conversation.",
  },
  leadersHr: {
    src: "/images/workplace-chaplaincy/leaders-hr.jpg",
    alt: "A leader and a supportive professional engaged in a private, focused conversation at a desk in an office setting.",
  },
  flexibleModel: {
    src: "/images/workplace-chaplaincy/flexible-model.jpg",
    alt: "A team of workers in a real outdoor worksite, representing the range of workforces CCCHAPS supports.",
  },
};

const SUPPORT_TOPICS = [
  "Grief and loss",
  "Workplace stress",
  "Marriage and relationship concerns",
  "Family and parenting challenges",
  "Serious illness or hospitalization",
  "Caregiving pressures",
  "Financial stress",
  "Burnout and emotional strain",
  "Conflict and difficult conversations",
  "Job changes and career transitions",
  "Major life changes",
  "Crisis events",
  "Spiritual or faith concerns",
  "Questions of meaning and purpose",
  "Support for family members",
  "Or simply needing someone to listen",
];

const CARE_MODELS = [
  {
    icon: MessageCircle,
    title: "Remote Care",
    body:
      "Confidential access through available phone, text, scheduled video, confidential message, and digital support pathways.",
  },
  {
    icon: Building2,
    title: "On-Site Chaplain Presence",
    body:
      "Scheduled workplace visits that allow chaplains to become familiar, accessible members of the organization's employee-support environment.",
  },
  {
    icon: Clock,
    title: "Crisis & On-Call Support",
    body:
      "24/7 on-call chaplain access for difficult events, urgent employee-support needs, grief, loss, and organizational crises.",
  },
];

function WCImage({ src, alt, position, aspect = "aspect-[5/6]" }) {
  return (
    <div
      className={`${aspect} rounded-2xl overflow-hidden shadow-sm border border-slate-200 bg-slate-100`}
      data-testid={`wc-image-slot-${position}`}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="w-full h-full object-cover"
      />
    </div>
  );
}

function SectionHeading({ eyebrow, children }) {
  return (
    <div className="mb-6">
      {eyebrow && (
        <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold mb-3">
          {eyebrow}
        </p>
      )}
      <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-slate-900">
        {children}
      </h2>
    </div>
  );
}

export default function WorkplaceChaplaincyPage() {
  usePageMeta({
    title:
      "Workplace Chaplaincy in Sacramento, Placer, El Dorado & Nevada Counties | California Corporate Chaplains",
    description:
      "Workplace chaplaincy from California Corporate Chaplains — confidential, non-clinical human support for employees and leaders. Scheduled onsite service in Sacramento, Placer, El Dorado, and Nevada Counties, plus remote chaplain support across California and nationwide, with 24/7 on-call access.",
  });

  return (
    <article
      className="bg-white"
      data-testid="service-detail-workplace-chaplaincy"
    >
      <BreadcrumbSchema
        id="workplace-chaplaincy"
        items={[
          { name: "Home", path: "/" },
          { name: "Services", path: "/services" },
          { name: "Workplace Chaplaincy", path: "/services/workplace-chaplaincy" },
        ]}
      />
      {/* ────────────────────── HERO ────────────────────── */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 md:pt-14 pb-10 md:pb-16">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#4A6FA5] mb-8"
            data-testid="wc-back-to-services"
          >
            <ArrowLeft className="w-4 h-4" /> All services
          </Link>

          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-7">
              <p className="text-xs uppercase tracking-[0.2em] text-[#4A6FA5] font-semibold mb-3">
                Service
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 leading-[1.05]">
                Workplace Chaplaincy
              </h1>
              <p className="mt-5 text-xl sm:text-2xl text-slate-700 font-heading leading-snug">
                Caring for the People Behind the Work
              </p>

              <div className="mt-8 space-y-5 text-base sm:text-lg text-slate-700 leading-relaxed">
                <p>
                  Every employee brings more than skills and responsibilities to
                  work. They also bring families, relationships, losses, health
                  concerns, financial pressures, personal transitions, and the
                  everyday challenges of life.
                </p>
                <p>
                  California Corporate Chaplains provides a trusted source of
                  confidential, human support for employees and the people who
                  lead them.
                </p>
                <p>
                  Through workplace presence and 24/7 on-call chaplain access,
                  CCCHAPS gives employees someone they can turn to when life
                  becomes difficult—and someone who can simply listen when that
                  is what they need most.
                </p>
              </div>

              <div className="mt-8 rounded-2xl border border-blue-100 bg-[#F0F4F8] px-6 py-5">
                <p className="text-base sm:text-lg text-slate-800 leading-relaxed">
                  Care is voluntary, confidential, respectful of every
                  background and belief, and available without requiring a
                  religious conversation.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/schedule?type=workplace_chaplaincy"
                  data-testid="wc-hero-cta-primary"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]"
                >
                  Schedule a Conversation <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/packages"
                  data-testid="wc-hero-cta-secondary"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg border border-slate-300 text-slate-800 font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5]"
                >
                  Build a Care Package
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <WCImage
                position="hero"
                src={IMAGES.hero.src}
                alt={IMAGES.hero.alt}
              />
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        {/* Workplace Challenges — The Challenges Employees Carry Into the Workplace */}
        <section
          className="mt-4 md:mt-6"
          data-testid="wc-workplace-challenges"
          data-analytics-section="workplace-challenges"
          aria-labelledby="wc-workplace-challenges-heading"
        >
          <SectionHeading eyebrow="Workplace realities">
            <span id="wc-workplace-challenges-heading">
              The Challenges Employees Carry Into the Workplace
            </span>
          </SectionHeading>
          <p className="text-base text-slate-700 leading-relaxed max-w-3xl">
            Employees do not stop being spouses, parents, caregivers, grieving
            family members, or people navigating difficult circumstances when
            they arrive at work. Personal and workplace pressures can affect
            focus, well-being, relationships, and the ability to stay engaged.
          </p>

          <div
            className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
            data-testid="wc-workplace-challenges-grid"
          >
            {WORKPLACE_CHALLENGE_CARDS.map((c, i) => {
              const Icon = c.icon;
              return (
                <article
                  key={c.label}
                  data-testid={`wc-workplace-challenge-${i}`}
                  className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#4A6FA5] hover:shadow-md transition flex flex-col"
                >
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-[#F0F4F8] text-[#4A6FA5]">
                    <Icon className="w-5 h-5" strokeWidth={1.75} />
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-medium text-slate-900">
                    {c.label}
                  </h3>
                  <p className="mt-2 text-sm text-slate-700 leading-relaxed flex-1">
                    {c.body}
                  </p>
                  {c.source && (
                    <p className="mt-4 text-[11px] text-slate-500 leading-relaxed">
                      Source:{" "}
                      <a
                        href={c.source.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#4A6FA5] hover:text-[#3B597B] underline decoration-slate-300 hover:decoration-[#4A6FA5] underline-offset-2"
                        data-testid={`wc-workplace-challenge-${i}-source`}
                      >
                        {c.source.label}
                      </a>
                    </p>
                  )}
                </article>
              );
            })}
          </div>

          <div
            className="mt-10 rounded-2xl border border-blue-100 bg-[#F0F4F8] p-6 md:p-8"
            data-testid="wc-workplace-challenges-callout"
          >
            <h3 className="font-heading text-xl sm:text-2xl tracking-tight text-slate-900">
              When Life Affects Work, Someone Should Be Available to Listen
            </h3>
            <p className="mt-3 text-slate-700 leading-relaxed max-w-3xl">
              CCCHAPS gives employees and their families access to confidential,
              non-clinical chaplain support through scheduled workplace
              presence, remote support, 24/7 on-call access, and the digital
              options included in their organization's care arrangement.
            </p>
            <p className="mt-4 text-sm text-[#3B597B] font-medium">
              Multiple ways to connect. One consistent source of support.
            </p>
            <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">
              CCCHAPS chaplaincy is non-clinical and does not provide
              psychotherapy, addiction or domestic-violence treatment, medical,
              or legal services. Chaplains help employees connect to
              appropriate professional or community resources when needs extend
              beyond chaplaincy scope.
            </p>
          </div>
        </section>

        {/* How Workplace Chaplaincy Works */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="How it works">
            How Workplace Chaplaincy Works
          </SectionHeading>
          <div className="space-y-5 text-base text-slate-700 leading-relaxed">
            <p>
              Workplace chaplaincy is built around something simple: presence
              and relationship.
            </p>
            <p>
              Rather than waiting until an employee reaches a crisis point,
              CCCHAPS chaplains become a familiar and approachable source of
              support. Depending on the organization's selected care model,
              chaplains may regularly visit the workplace, connect with
              employees remotely, provide follow-up support, and remain
              available when unexpected situations arise.
            </p>
            <p>
              Employees can connect through multiple pathways, including phone,
              text, confidential message, scheduled video when available, and
              in-person chaplain visits.
            </p>
            <p>
              Through CALLACHAP, employees have access to confidential chaplain
              support beyond the scheduled workplace visit. The CCCHAPS Care
              Portal provides additional pathways to request support and access
              available resources, while CCCHAPS On Demand extends supportive
              digital resources beyond the immediate conversation.
            </p>
            <p>
              The goal is not to force interaction. Chaplain support is
              permission-based. Employees decide whether they want to talk,
              what they want to discuss, and whether spiritual support is part
              of that conversation.
            </p>
          </div>
        </section>

        {/* Built on Trust and Consistent Presence */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="Trust & presence">
            Built on Trust and Consistent Presence
          </SectionHeading>

          <div className="grid md:grid-cols-5 gap-8 items-start">
            <div className="md:col-span-3 space-y-5 text-base text-slate-700 leading-relaxed">
              <p>
                Trust cannot be created by a brochure or a phone number alone.
                It develops when people know who is available to them and
                experience that person as consistent, respectful, and
                trustworthy.
              </p>
              <p>That is why workplace presence remains an important part of the CCCHAPS model.</p>
              <p>
                A chaplain may walk through a worksite, sit with an employee
                during a break, check in after a difficult event, visit an
                employee or family when appropriate, or simply become a
                familiar person employees know they can approach.
              </p>
              <p>
                Not every conversation begins with a crisis. Sometimes an
                employee just needs someone outside the organizational chain
                of command who will listen without judgment.
              </p>
              <p>
                Over time, consistent presence can make it easier for
                employees to reach out when they are carrying something
                difficult.
              </p>
            </div>
            <div className="md:col-span-2">
              <WCImage
                position="trust-presence"
                aspect="aspect-[4/5]"
                src={IMAGES.trustPresence.src}
                alt={IMAGES.trustPresence.alt}
              />
            </div>
          </div>
        </section>

        {/* Support That Goes Beyond Business Hours */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="24/7 access">
            Support That Goes Beyond Business Hours
          </SectionHeading>
          <div className="space-y-5 text-base text-slate-700 leading-relaxed">
            <p>Life does not operate on a Monday-through-Friday schedule.</p>
            <p>
              A family crisis can happen at night. A death may occur over the
              weekend. An employee may receive difficult news after leaving
              work. A leader may face an unexpected workplace event when the
              regularly scheduled chaplain visit is days away.
            </p>
            <p>
              CCCHAPS provides 24/7 on-call chaplain access so employees and
              organizational leaders have a pathway to support beyond normal
              business hours.
            </p>
            <p>
              Depending on the situation and the organization's care
              arrangement, support may begin by phone, text, confidential
              message, or another appropriate connection method, with
              additional follow-up when needed.
            </p>
            <p className="text-sm text-slate-600 border-l-2 border-slate-200 pl-4">
              CCCHAPS is not an emergency medical or psychiatric response
              service. Life-threatening emergencies should be directed to 911
              or the appropriate emergency resource.
            </p>
          </div>

          <AccessModelCallout
            variant="default"
            className="mt-10"
            testId="wc-access-model-callout"
          />
        </section>

        {/* Someone to Turn to When Life Gets Difficult */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="Human support">
            Someone to Turn to When Life Gets Difficult
          </SectionHeading>
          <div className="space-y-5 text-base text-slate-700 leading-relaxed">
            <p>People do not always need someone to fix the problem.</p>
            <p>Sometimes they need someone who will sit with them in it.</p>
            <p>
              CCCHAPS chaplains provide a confidential space where employees
              can talk through personal concerns, family pressures, grief,
              workplace stress, difficult decisions, questions of faith or
              meaning, or simply the weight of what they are carrying.
            </p>
            <p>
              When an employee requests spiritual care, the chaplain can
              provide appropriate spiritual support consistent with the
              employee's beliefs and preferences.
            </p>
            <p>
              When a need extends beyond the chaplain's scope, the chaplain
              can encourage connection with appropriate resources such as an
              EAP, licensed mental-health professional, healthcare provider,
              community organization, or other specialized support.
            </p>
            <p>
              The chaplain does not replace those professionals. The chaplain
              helps ensure the employee does not have to navigate a difficult
              moment alone.
            </p>
          </div>
        </section>

        {/* Clear Boundaries. Respectful Care. */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="Boundaries">
            Clear Boundaries. Respectful Care.
          </SectionHeading>
          <div className="space-y-5 text-base text-slate-700 leading-relaxed">
            <p>
              CCCHAPS workplace chaplaincy is built around respect for
              personal choice.
            </p>
            <p>
              Chaplains do not impose religious beliefs, pressure employees
              into conversations, diagnose mental-health conditions, provide
              clinical treatment, or function as an extension of management.
            </p>
            <p>Spiritual conversations occur when welcomed by the employee.</p>
            <p>
              Personal conversations are treated as confidential within
              appropriate professional, ethical, safety, and legal limits.
              Information shared with a chaplain is not used as an employee
              performance-management tool.
            </p>
            <p>
              The chaplain occupies a distinct position: supportive of the
              organization while providing employees with a trusted care
              relationship outside the normal supervisory chain.
            </p>
            <p className="font-medium text-slate-900">That distinction matters.</p>
          </div>
        </section>

        {/* Support for Life's Challenges */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="What employees bring">
            Support for Life's Challenges
          </SectionHeading>
          <p className="text-base text-slate-700 leading-relaxed mb-6">
            Employees may reach out to a CCCHAPS chaplain for many reasons,
            including:
          </p>
          <ul
            className="grid sm:grid-cols-2 gap-x-6 gap-y-3"
            data-testid="wc-support-topics"
          >
            {SUPPORT_TOPICS.map((t) => (
              <li key={t} className="flex items-start gap-3 text-slate-700">
                <Check
                  className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5"
                  strokeWidth={2}
                />
                <span className="leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
          <p className="mt-8 text-base sm:text-lg font-medium text-slate-900 leading-relaxed">
            An employee does not need to be in crisis—and does not need to be
            religious—to speak with a chaplain.
          </p>
        </section>

        {/* Support for Leaders and HR Teams */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="Leaders & HR">
            Support for Leaders and HR Teams
          </SectionHeading>
          <div className="grid md:grid-cols-5 gap-8 items-start">
            <div className="md:col-span-3 space-y-5 text-base text-slate-700 leading-relaxed">
              <p>
                Leaders care about their people, but they cannot—and should
                not—be expected to personally carry every employee's private
                struggle.
              </p>
              <p>
                There are times when a supervisor or HR professional
                recognizes that an employee is hurting but knows the
                situation requires a different kind of support.
              </p>
              <p>A workplace chaplain provides another appropriate pathway.</p>
              <p>
                With the employee's permission, chaplains can help employees
                identify additional resources when specialized assistance is
                needed. Chaplaincy can also complement existing organizational
                resources such as EAPs, mental-health benefits, wellness
                programs, HR support, and community services.
              </p>
              <p>
                CCCHAPS also recognizes that leaders themselves need
                confidential support. Executives, supervisors, HR
                professionals, and business owners carry responsibilities that
                can be difficult to discuss with the people they lead.
              </p>
              <p className="font-medium text-slate-900">
                Chaplain support is available to them too.
              </p>
            </div>
            <div className="md:col-span-2">
              <WCImage
                position="leaders-hr"
                aspect="aspect-[4/5]"
                src={IMAGES.leadersHr.src}
                alt={IMAGES.leadersHr.alt}
              />
            </div>
          </div>
        </section>

        {/* Privacy & Accountability */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="Privacy & accountability">
            Care for Employees. Respect for Privacy. Accountability for
            Organizations.
          </SectionHeading>
          <div className="space-y-5 text-base text-slate-700 leading-relaxed">
            <p>
              Organizations need to know whether the services they provide are
              being used. Employees need to know their personal conversations
              remain private.
            </p>
            <p>CCCHAPS is designed to respect both needs.</p>
            <p>
              Internal chaplain care is documented appropriately for
              continuity, professional accountability, and service management.
              Employer-facing utilization reporting remains aggregate and
              non-identifying.
            </p>
            <p>
              Organizations can understand areas such as service utilization
              and types of support being provided without receiving the
              private details of an employee's conversation.
            </p>
          </div>

          <div className="mt-8 rounded-2xl bg-[#1F3A5F] text-white p-8 md:p-10 shadow-md flex items-start gap-4">
            <ShieldCheck
              className="w-8 h-8 shrink-0 mt-1 opacity-90"
              strokeWidth={1.75}
            />
            <p className="font-heading text-xl sm:text-2xl leading-snug tracking-tight">
              The organization sees the impact. The employee keeps the
              conversation.
            </p>
          </div>
        </section>

        {/* Flexible Care Model */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="Flexible model">
            A Flexible Care Model for Different Workplaces
          </SectionHeading>
          <div className="grid md:grid-cols-5 gap-8 items-start">
            <div className="md:col-span-3 space-y-5 text-base text-slate-700 leading-relaxed">
              <p>Every organization is different.</p>
              <p>
                A healthcare organization does not operate like a construction
                company. A restaurant workforce has different rhythms from an
                office environment. A senior living community has different
                needs from an automotive dealership.
              </p>
              <p>
                CCCHAPS therefore does not assume that one workplace
                chaplaincy model fits every organization.
              </p>
              <p>
                Organizations can build a care arrangement around their
                workforce and operating environment, including:
              </p>
            </div>
            <div className="md:col-span-2">
              <WCImage
                position="flexible-model"
                aspect="aspect-[4/5]"
                src={IMAGES.flexibleModel.src}
                alt={IMAGES.flexibleModel.alt}
              />
            </div>
          </div>

          <div className="mt-8 grid md:grid-cols-3 gap-5" data-testid="wc-care-models">
            {CARE_MODELS.map((m) => {
              const Icon = m.icon;
              return (
                <article
                  key={m.title}
                  className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
                >
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#F0F4F8] text-[#4A6FA5] mb-4">
                    <Icon className="w-5 h-5" strokeWidth={1.75} />
                  </span>
                  <h3 className="font-heading text-lg text-slate-900">
                    {m.title}
                  </h3>
                  <p className="mt-3 text-sm text-slate-700 leading-relaxed">
                    {m.body}
                  </p>
                </article>
              );
            })}
          </div>

          <p className="mt-8 text-base text-slate-700 leading-relaxed">
            Every CCCHAPS care package includes access to the core support
            infrastructure provided through CALLACHAP, the CCCHAPS Care
            Portal, CCCHAPS On Demand, and 24/7 on-call chaplain support.
          </p>
        </section>

        {/* Closing */}
        <section className="mt-16 md:mt-20">
          <SectionHeading eyebrow="About people">
            Workplace Chaplaincy Is About People
          </SectionHeading>
          <div className="space-y-5 text-base text-slate-700 leading-relaxed">
            <p>
              Organizations invest heavily in the systems that keep work
              moving.
            </p>
            <p>Workplace chaplaincy invests in the people doing the work.</p>
            <p>
              It creates a place for the conversation that does not fit
              neatly into a staff meeting, HR office, benefits portal, or
              performance review.
            </p>
            <ul className="space-y-2 pt-2">
              <li className="flex items-start gap-3 text-slate-800">
                <Compass className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5" strokeWidth={1.75} />
                <span>A place to talk.</span>
              </li>
              <li className="flex items-start gap-3 text-slate-800">
                <Compass className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5" strokeWidth={1.75} />
                <span>A person who will listen.</span>
              </li>
              <li className="flex items-start gap-3 text-slate-800">
                <Compass className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5" strokeWidth={1.75} />
                <span>Support when life becomes heavy.</span>
              </li>
              <li className="flex items-start gap-3 text-slate-800">
                <Compass className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5" strokeWidth={1.75} />
                <span>
                  And a trusted presence employees and leaders know they can
                  turn to when they need it.
                </span>
              </li>
            </ul>
            <p className="mt-6 font-heading text-lg text-slate-900 leading-snug">
              California Corporate Chaplains — Caring for the people behind
              the work.
            </p>
          </div>
        </section>

        {/* Final CTA */}
        <section
          className="mt-14 rounded-2xl bg-[#F0F4F8] border border-blue-100 p-6 md:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
          data-testid="wc-final-cta"
        >
          <div>
            <h3 className="font-heading text-xl font-medium text-slate-900">
              Ready to talk?
            </h3>
            <p className="text-sm text-slate-600 mt-1">
              Confidential consultation — no pitch, no obligation.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/schedule?type=workplace_chaplaincy"
              data-testid="wc-final-cta-schedule"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B] whitespace-nowrap"
            >
              Schedule a Conversation <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/packages"
              data-testid="wc-final-cta-packages"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg border border-slate-300 text-slate-800 font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5] whitespace-nowrap"
            >
              Build a Care Package
            </Link>
          </div>
        </section>
      </div>
    </article>
  );
}
